import type { ReactNode, RefObject } from 'react';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { MONTH_NAMES } from './date';
import type { CalendarMonth } from './types';

export type CalendarView = 'days' | 'months' | 'years';
export type GridView = Exclude<CalendarView, 'days'>;

export interface PagingNav {
    unit: string;
    onPrev: () => void;
    onNext: () => void;
    prevDisabled: boolean;
    nextDisabled: boolean;
}

const NAV_BUTTON_CLASS =
    'h-7 w-7 grid place-items-center rounded-lg text-gray-500 hover:bg-gray-50 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent';

interface ViewToggleProps {
    buttonRef: RefObject<HTMLButtonElement>;
    open: boolean;
    disabled?: boolean;
    onClick: () => void;
    children: ReactNode;
}

function ViewToggle({ buttonRef, open, disabled, onClick, children }: ViewToggleProps) {
    return (
        <button
            ref={buttonRef}
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-expanded={open}
            className="inline-flex items-center gap-0.5 rounded-lg px-1 hover:bg-gray-50 disabled:cursor-default disabled:hover:bg-transparent"
        >
            {children}
            <ChevronDownIcon
                className={`h-3 w-3 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
            />
        </button>
    );
}

interface MiniCalendarHeaderProps {
    month: CalendarMonth;
    view: CalendarView;
    disabled?: boolean;
    onToggleView: (view: GridView) => void;
    toggleRefs: Record<GridView, RefObject<HTMLButtonElement>>;
    /** Null hides the paging chevrons (the month grid has nothing to page). */
    nav: PagingNav | null;
}

export function MiniCalendarHeader({
    month,
    view,
    disabled,
    onToggleView,
    toggleRefs,
    nav
}: MiniCalendarHeaderProps) {
    return (
        <div className="flex items-center justify-between mb-3 h-7">
            <div className="-ml-1 flex items-center text-sm font-semibold text-gray-800">
                <ViewToggle
                    buttonRef={toggleRefs.months}
                    open={view === 'months'}
                    disabled={disabled}
                    onClick={() => onToggleView('months')}
                >
                    {MONTH_NAMES[month.month]}
                </ViewToggle>
                <ViewToggle
                    buttonRef={toggleRefs.years}
                    open={view === 'years'}
                    disabled={disabled}
                    onClick={() => onToggleView('years')}
                >
                    {month.year}
                </ViewToggle>
            </div>
            {nav && (
                <div className={`flex gap-1 ${disabled ? 'opacity-40 pointer-events-none' : ''}`}>
                    <button
                        type="button"
                        onClick={nav.onPrev}
                        disabled={disabled || nav.prevDisabled}
                        aria-label={`Previous ${nav.unit}`}
                        className={NAV_BUTTON_CLASS}
                    >
                        <ChevronLeftIcon className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        onClick={nav.onNext}
                        disabled={disabled || nav.nextDisabled}
                        aria-label={`Next ${nav.unit}`}
                        className={NAV_BUTTON_CLASS}
                    >
                        <ChevronRightIcon className="h-4 w-4" />
                    </button>
                </div>
            )}
        </div>
    );
}
